import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react';
const testimonials = [{
  quote: "In one day, with Scott's help, I was able to formulate a much more educated opinion about an industry important to my portfolio company. Scott is a go to guy in executive search.",
  name: 'Jim Armstrong',
  title: 'Partner, Clearstone Venture Partners',
  image: 'https://cdn.magicpatterns.com/uploads/43sXNCBRcgP9GQweX2XbiA/eu2uiv6frdotjdb8i7rw.png.png'
}, {
  quote: "Having worked with Whiterock for over a decade, I've not found a better partner in helping build high performing leadership teams. They invest the time to learn your business, team and objectives to ensure you ultimately select the right candidate.",
  name: 'Derek Hutson',
  title: 'CEO, Knack',
  image: 'https://cdn.magicpatterns.com/uploads/d5oL9Sss5o7oTzcjUYysNJ/f092c086-1c47-49ba-9790-fd4bec131e67_medium-1.jpg'
}];
interface TestimonialsProps {
  variant?: 'light' | 'dark';
}
export function Testimonials({
  variant = 'light'
}: TestimonialsProps) {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(0);
  const next = () => {
    setDirection(1);
    setCurrent(prev => (prev + 1) % testimonials.length);
  };
  const prev = () => {
    setDirection(-1);
    setCurrent(prev => (prev - 1 + testimonials.length) % testimonials.length);
  };
  // Auto-advance every 8 seconds
  useEffect(() => {
    const timer = setInterval(next, 8000);
    return () => clearInterval(timer);
  }, []);
  const bgClass = variant === 'dark' ? 'bg-gray-900' : 'bg-white';
  const textClass = variant === 'dark' ? 'text-white' : 'text-gray-900';
  const subtextClass = variant === 'dark' ? 'text-gray-400' : 'text-gray-500';
  const quoteTextClass = variant === 'dark' ? 'text-gray-300' : 'text-gray-700';
  const dotActiveClass = variant === 'dark' ? 'bg-white' : 'bg-primary';
  const dotInactiveClass = variant === 'dark' ? 'bg-gray-600' : 'bg-gray-300';
  const btnClass = variant === 'dark' ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:text-primary hover:bg-gray-100';
  const slideVariants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 80 : -80,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (dir: number) => ({
      x: dir > 0 ? -80 : 80,
      opacity: 0
    })
  };
  const t = testimonials[current];
  return <section className={`py-20 ${bgClass}`} data-id="element-96">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-97">
        <div className="text-center mb-12" data-id="element-98">
          <h3 className={`text-sm font-bold uppercase tracking-widest ${subtextClass}`} data-id="element-99">
            What Our Clients Say
          </h3>
        </div>

        <div className="relative" data-id="element-100">
          <AnimatePresence mode="wait" custom={direction} data-id="element-101">
            <motion.div key={current} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
            duration: 0.4,
            ease: 'easeInOut'
          }} className="flex flex-col md:flex-row items-center gap-10 md:gap-16" data-id="element-102">
              {/* Photo */}
              <div className="flex-shrink-0" data-id="element-103">
                <div className="w-36 h-36 md:w-44 md:h-44 rounded-full overflow-hidden grayscale" data-id="element-104">
                  <img src={t.image} alt={t.name} className="w-full h-full object-cover object-center" data-id="element-105" />
                </div>
              </div>

              {/* Quote */}
              <div className="flex-1 text-center md:text-left" data-id="element-106">
                <Quote className="w-8 h-8 text-primary/30 mb-4 hidden md:block" data-id="element-107" />
                <p className={`text-xl md:text-2xl leading-relaxed mb-6 ${quoteTextClass}`} data-id="element-108">
                  {t.quote}
                </p>
                <div data-id="element-109">
                  <p className={`text-lg font-bold ${textClass}`} data-id="element-110">{t.name}</p>
                  <p className={`text-sm ${subtextClass}`} data-id="element-111">{t.title}</p>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex items-center justify-center md:justify-end mt-10 gap-4" data-id="element-112">
            <button onClick={prev} className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${btnClass}`} aria-label="Previous testimonial" data-id="element-113">
              <ChevronLeft className="w-5 h-5" data-id="element-114" />
            </button>

            <div className="flex gap-2" data-id="element-115">
              {testimonials.map((_, i) => <button key={i} onClick={() => {
              setDirection(i > current ? 1 : -1);
              setCurrent(i);
            }} className={`w-2 h-2 rounded-full transition-all duration-300 ${i === current ? `${dotActiveClass} w-6` : dotInactiveClass}`} aria-label={`Go to testimonial ${i + 1}`} data-id="element-116" />)}
            </div>

            <button onClick={next} className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${btnClass}`} aria-label="Next testimonial" data-id="element-117">
              <ChevronRight className="w-5 h-5" data-id="element-118" />
            </button>
          </div>
        </div>
      </div>
    </section>;
}